import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | error
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch (e) { /* keep trying */ }
      tries.current += 1;
      if (tries.current > 12) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center rounded-3xl border border-border bg-white p-10 shadow-sm" data-testid="payment-success-page">
        {state === "polling" && (<><Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" /><h1 className="font-display text-2xl font-bold">Confirming your payment…</h1><p className="text-muted-foreground mt-2">Please wait a moment.</p></>)}
        {state === "success" && (<>
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 text-white flex items-center justify-center mx-auto mb-4"><Crown className="w-8 h-8" /></div>
          <h1 className="font-display text-2xl font-bold">Welcome to OyeBest Premium! ⭐</h1>
          <p className="text-muted-foreground mt-2">Your Premium membership is now active. The Premium badge and priority placement are live on your listing.</p>
          <Button className="mt-6 rounded-full" onClick={() => navigate("/owner/dashboard")} data-testid="payment-goto-dashboard"><CheckCircle2 className="w-4 h-4 mr-2" /> Go to Dashboard</Button>
        </>)}
        {state === "error" && (<>
          <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4"><XCircle className="w-8 h-8" /></div>
          <h1 className="font-display text-2xl font-bold">Payment not confirmed</h1>
          <p className="text-muted-foreground mt-2">We couldn't confirm your payment yet. If money was deducted it will reflect shortly, or try again.</p>
          <Button variant="outline" className="mt-6 rounded-full" onClick={() => navigate("/owner/dashboard")}>Back to Dashboard</Button>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center rounded-3xl border border-border bg-white p-10 shadow-sm" data-testid="payment-cancel-page">
        <div className="w-16 h-16 rounded-full bg-secondary text-muted-foreground flex items-center justify-center mx-auto mb-4"><XCircle className="w-8 h-8" /></div>
        <h1 className="font-display text-2xl font-bold">Payment cancelled</h1>
        <p className="text-muted-foreground mt-2">No charge was made. You can upgrade to Premium anytime from your dashboard.</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/owner/dashboard")}>Back to Dashboard</Button>
      </div>
    </div>
  );
}
